// Shared helpers for treatment / chart / oral-exam rendering. Used by History
// list + Detail page + (later) Summary dashboard.

export function asList(v) {
  if (Array.isArray(v)) return v
  if (Array.isArray(v?.items)) return v.items
  if (Array.isArray(v?.data)) return v.data
  if (Array.isArray(v?.results)) return v.results
  return []
}

export function parseDateObj(v) {
  if (!v) return null
  if (typeof v === 'number') return new Date(v)
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return new Date(s.slice(0, 10) + 'T00:00:00')
  const d = new Date(s)
  return isNaN(d) ? null : d
}

export function normalizeDate(v) {
  const d = parseDateObj(v)
  return d ? d.toISOString().slice(0, 10) : ''
}

const THAI_MONTHS_FULL = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
]

export function formatThaiDate(v) {
  const d = parseDateObj(v)
  if (!d) return '—'
  const weekday = d.toLocaleDateString('th-TH', { weekday: 'long' })
  return `${weekday}ที่ ${d.getDate()} ${THAI_MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}`
}

/** "14:30 — 15:30" from ISO timestamps or HH:MM strings. */
export function formatTimeRange(start, end) {
  const s = parseHM(start)
  const e = parseHM(end)
  if (!s && !e) return ''
  if (s && e) return `${s} — ${e}`
  return s || e
}

export function parseHM(v) {
  if (!v) return ''
  if (typeof v === 'number') {
    const d = new Date(v)
    if (isNaN(d)) return ''
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }
  const s = String(v)
  // ISO timestamp
  const m = s.match(/T?(\d{2}):(\d{2})/)
  if (m) return `${m[1]}:${m[2]}`
  return ''
}

/** Duration in minutes between two timestamps, if computable. */
export function computeDurationMin(start, end, fallback) {
  if (fallback != null && !isNaN(fallback)) return Number(fallback)
  if (!start || !end) return null
  const a = new Date(start).getTime()
  const b = new Date(end).getTime()
  if (isNaN(a) || isNaN(b)) return null
  return Math.round((b - a) / 60000)
}

/** Xano file-field unwrapping. */
export function pickImageUrl(v) {
  if (!v) return ''
  if (typeof v === 'string') return v.trim()
  if (typeof v === 'object') {
    return String(v.url || v.path || v.src || v.file?.url || '').trim()
  }
  return ''
}

export function getDoctorImage(t) {
  const doctor = t?.doctor || {}
  const candidates = [
    // Real Xano shape uses `profile_img` (no e); other shapes covered as fallback.
    doctor.profile_img,
    doctor.profile_image, doctor.profileImage,
    doctor.image, doctor.photo, doctor.avatar, doctor.picture,
    t?.doctor_profile_img, t?.doctor_profile_image, t?.doctor_image, t?.doctorImage,
  ]
  for (const c of candidates) {
    const url = pickImageUrl(c)
    if (url) return url
  }
  return ''
}

/** Pretty colors for the appointmentlist chip from a hex. */
export function chipStyles(hex) {
  if (!hex || typeof hex !== 'string' || !/^#?[0-9a-fA-F]{6}$/.test(hex.replace('#', ''))) {
    return { backgroundColor: '#f1f5f9', color: '#475569', boxShadow: 'inset 0 0 0 1px rgba(148,163,184,0.25)' }
  }
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const bg   = `rgba(${r}, ${g}, ${b}, 0.10)`
  const ring = `rgba(${r}, ${g}, ${b}, 0.28)`
  const text = `rgb(${Math.round(r * 0.55)}, ${Math.round(g * 0.55)}, ${Math.round(b * 0.55)})`
  return { backgroundColor: bg, color: text, boxShadow: `inset 0 0 0 1px ${ring}` }
}

/**
 * Build a chart-lookup keyed by treatment_id (preferred). Real Xano shape uses
 * `chart.treatment` (not `treatment_id`), `chart.patient`, `chart.doctor`.
 *
 * IMPORTANT: A single treatment can have MULTIPLE chart rows (orphan / draft
 * duplicates). When that happens we keep the *richest* chart — the one with
 * the most actual content (history fields, hx entries, documents, etc.).
 */
export function buildChartIndex(charts) {
  const byTreatment = new Map()
  for (const c of charts) {
    if (!c) continue
    const tid = c.treatment ?? c.treatment_id
    if (tid == null || tid === 0) continue
    const key = String(tid)
    const cur = byTreatment.get(key)
    if (!cur || chartRichness(c) > chartRichness(cur)) byTreatment.set(key, c)
  }
  return {
    findFor(t) {
      if (!t) return null
      if (t.chart && typeof t.chart === 'object' && !Array.isArray(t.chart)) return t.chart
      const tid = t.id ?? t.treatment_id ?? t.treatment
      if (tid != null && byTreatment.has(String(tid))) return byTreatment.get(String(tid))
      return null
    },
  }
}

/**
 * Score a chart by how much real content it has. Used to break ties when
 * multiple chart rows share a treatment_id (Xano allows duplicates).
 */
function chartRichness(c) {
  if (!c) return 0
  let score = 0
  const h = c.history || {}
  for (const k of ['cc', 'PE', 'NV', 'price', 'note']) {
    const v = h[k]
    if (v != null && String(v).trim() !== '') score += 2
  }
  if (h.NV_duration && Number(h.NV_duration) > 0) score += 1
  score += (Array.isArray(c.hx)         ? c.hx.length         : 0) * 3
  score += (Array.isArray(c.document)   ? c.document.length   : 0) * 1
  score += (Array.isArray(c.consent)    ? c.consent.length    : 0) * 1
  score += (Array.isArray(c.oral_exam)  ? c.oral_exam.length  : 0) * 2
  score += (Array.isArray(c.chat)       ? c.chat.length       : 0) * 0.5
  return score
}

/**
 * Returns true only when a chart has a real Full Mouth Exam finding — i.e. a
 * `chart.oral_exam[]` entry carrying an actual `type` or `detail`. Clinical
 * `hx[]` entries do NOT count (that's the "Oral Exam & Treatment" card), and
 * empty/blank oral_exam entries are ignored so the blue dot stays hidden when
 * the Full Mouth Exam is empty.
 */
export function hasOralExam(chart) {
  if (!chart) return false
  const arr = Array.isArray(chart.oral_exam) ? chart.oral_exam : []
  const nonEmpty = (v) => {
    if (v == null) return false
    if (Array.isArray(v)) return v.length > 0
    return String(v).trim() !== ''
  }
  return arr.some((e) => e && typeof e === 'object' && (nonEmpty(e.type) || nonEmpty(e.detail)))
}

/**
 * Normalize an hx[] entry's tag info. Real Xano data is inconsistent — older
 * rows put the tag-type STRING inside `tag[0]` (e.g. tag = ["Full Mouth"]) and
 * leave tag_type empty. Newer rows correctly set tag_type and put tooth numbers
 * in tag[]. This helper returns `{ tag_type, teeth: string[] }` from either.
 */
export function normalizeHxTag(entry) {
  const rawType  = entry?.tag_type || entry?.tagType || ''
  const rawTag   = Array.isArray(entry?.tag) ? entry.tag.map(String) : []
  const TYPE_STRINGS = ['Adult', 'Ped', 'Pedo', 'Ortho', 'Full Mouth', 'FM']
  // If tag_type is already set, trust it.
  if (rawType) {
    return {
      tag_type: rawType === 'Pedo' ? 'Ped' : rawType === 'FM' ? 'Full Mouth' : rawType,
      teeth:    rawType === 'Adult' || rawType === 'Ped' || rawType === 'Pedo' ? rawTag : [],
    }
  }
  // Legacy: tag[0] holds the type string.
  if (rawTag.length > 0 && TYPE_STRINGS.includes(rawTag[0])) {
    const t = rawTag[0]
    return {
      tag_type: t === 'Pedo' ? 'Ped' : t === 'FM' ? 'Full Mouth' : t,
      teeth:    rawTag.slice(1),
    }
  }
  // Numeric tooth numbers → infer from prefix.
  if (rawTag.length > 0) {
    const first = rawTag[0]
    const q = String(first).charAt(0)
    if (['1', '2', '3', '4'].includes(q)) return { tag_type: 'Adult', teeth: rawTag }
    if (['5', '6', '7', '8'].includes(q)) return { tag_type: 'Ped',   teeth: rawTag }
  }
  return { tag_type: 'Full Mouth', teeth: [] }
}

/** UI label/color for the 4 tooth-tag types. */
export const TAG_TYPE_META = {
  'Adult':      { label: 'Adult',      bg: 'bg-blue-50',    text: 'text-blue-700',    ring: 'ring-blue-200/80' },
  'Ped':        { label: 'Pedo',       bg: 'bg-violet-50',  text: 'text-violet-700',  ring: 'ring-violet-200/80' },
  'Ortho':      { label: 'Ortho',      bg: 'bg-pink-50',    text: 'text-pink-700',    ring: 'ring-pink-200/80' },
  'Full Mouth': { label: 'Full Mouth', bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200/80' },
}

export function tagTypeMeta(t) {
  return TAG_TYPE_META[t] || { label: t || '—', bg: 'bg-slate-50', text: 'text-slate-700', ring: 'ring-slate-200' }
}
